import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { catchError, forkJoin, map, Observable, of, switchMap, Subject, takeUntil } from 'rxjs';
import { RestService } from '../../../core/services/rest.service';
import { ExpandableAccordionComponent } from '../../common/expandable-accordion/expandable-accordion.component';
import { FieldMappingComponent, FieldMappingField } from '../../common/field-mapping/field-mapping.component';
import { buildZephyrFolderTree, ZephyrFolder, ZephyrFolderRecord, ZephyrTestCase } from './zephyr-folders.data';

export type ZephyrModalAction = 'back';

export interface ZephyrImportedResult {
  imported: true;
  connectionId: string;
  connectionName: string;
  fieldMappings: Record<string, { source: string; field: string }>;
  extractedTestCases: any[];
}

export type ZephyrModalResult = ZephyrModalAction | ZephyrImportedResult;

interface ZephyrAccordionColumn {
  mapping_key: string;
  display_name: string;
  isVisible: boolean;
}

interface ZephyrAccordionRow {
  _id: string;
  userStoryId: string;
  key: string;
  testCaseId: string;
  testCase: string;
  status: ZephyrTestCase['status'];
  folderName: string;
  folderId: string;
}

@Component({
  selector: 'app-import-zephyr-modal',
  standalone: true,
  imports: [CommonModule, ExpandableAccordionComponent, FieldMappingComponent],
  templateUrl: './import-zephyr-modal.component.html',
  styleUrl: './import-zephyr-modal.component.scss',
})
export class ImportZephyrModalComponent implements OnInit, OnDestroy {
  readonly activeModal = inject(NgbActiveModal);
  private readonly document = inject(DOCUMENT);
  private readonly restService = inject(RestService);
  private readonly destroy$ = new Subject<void>();

  @Input() projectId = '';
  @Input() connectionId = '';
  @Input() connectionName = '';

  folders: ZephyrFolder[] = [];
  isLoadingFolders = false;
  foldersError = '';
  mappingFields: FieldMappingField[] = [];
  mappingColumns: string[] = [];
  isLoadingMapping = false;
  isImporting = false;
  mappingError = '';
  private extractedColumns: { source: string; field: string }[] = [];

  step: 2 | 3 = 2;
  isActive = false;
  fieldMappings: Record<string, string> = {};
  selectedFolderId = '';
  selectedTestCaseCount = 0;
  selectedFolderCount = 0;
  retainedSelectionIds: string[] = [];
  testCaseView: { columns: ZephyrAccordionColumn[]; rows: ZephyrAccordionRow[] } = {
    columns: [],
    rows: [],
  };

  private readonly expandedIds = new Set<string>();
  private readonly selectedCases = new Map<string, ZephyrAccordionRow>();
  private readonly accordionColumns: ZephyrAccordionColumn[] = [
    { mapping_key: 'userStoryId', display_name: 'Folder', isVisible: true },
    { mapping_key: 'testCaseId', display_name: 'Test Case ID', isVisible: true },
    { mapping_key: 'testCase', display_name: 'Test Case', isVisible: true },
    { mapping_key: 'status', display_name: 'Status', isVisible: false },
    { mapping_key: 'folderName', display_name: 'Folder', isVisible: false },
  ];

  ngOnInit(): void {
    this.loadFolders();
  }

  ngOnDestroy(): void {
    this.syncDialogSize(false);
    this.destroy$.next();
    this.destroy$.complete();
  }

  onBack(): void {
    if (this.isImporting || this.isLoadingMapping) {
      return;
    }
    if (this.step === 3) {
      this.step = 2;
      this.syncDialogSize();
      return;
    }
    const action: ZephyrModalAction = 'back';
    this.activeModal.close(action);
  }

  onContinue(): void {
    if (this.step === 2) {
      this.loadFieldMapping();
      return;
    }
    this.saveMapping();
  }

  get canContinueMapping(): boolean {
    return this.mappingFields
      .filter((field) => field.required)
      .every((field) => !!this.fieldMappings[field.fieldKey]);
  }

  get continueDisabled(): boolean {
    if (this.isLoadingMapping || this.isImporting) {
      return true;
    }
    if (this.step === 2) {
      return this.selectedTestCaseCount === 0;
    }
    return !this.canContinueMapping;
  }

  get continueLabel(): string {
    if (this.step === 2) {
      return 'Continue';
    }
    return this.isImporting ? 'Importing...' : 'Import';
  }

  /** Only send filled mappings, using source and field from preview-columns. */
  get activeColumnMappings(): Record<string, { source: string; field: string }> {
    const mappings: Record<string, { source: string; field: string }> = {};
    for (const [fieldKey, fieldName] of Object.entries(this.fieldMappings)) {
      const field = fieldName?.trim();
      if (!field) {
        continue;
      }
      const column = this.extractedColumns.find((item) => item.field === field);
      mappings[fieldKey] = {
        source: column?.source ?? 'FILE',
        field,
      };
    }
    return mappings;
  }

  selectFolder(folder: ZephyrFolder): void {
    this.selectedFolderId = folder.id;
    if (folder.children.length > 0 && !this.expandedIds.has(folder.id)) {
      this.expandedIds.add(folder.id);
    }
    this.refreshTestCases();
  }

  toggleFolder(folder: ZephyrFolder, event: Event): void {
    event.stopPropagation();
    if (this.expandedIds.has(folder.id)) {
      this.expandedIds.delete(folder.id);
      return;
    }
    this.expandedIds.add(folder.id);
  }

  isExpanded(folderId: string): boolean {
    return this.expandedIds.has(folderId);
  }

  isSelected(folderId: string): boolean {
    return this.selectedFolderId === folderId;
  }

  folderMeta(folder: ZephyrFolder): string {
    const childFolders = folder.children.length;
    const testCases = this.countTestCases(folder);
    const folderLabel = childFolders === 1 ? '1 folder' : `${childFolders} folders`;
    const caseLabel = testCases === 1 ? '1 test case' : `${testCases} test cases`;
    if (childFolders === 0) {
      return caseLabel;
    }
    return `${folderLabel} · ${caseLabel}`;
  }

  trackFolder(_index: number, folder: ZephyrFolder): string {
    return folder.id;
  }

  /** Selected cases owned by this folder or any folder under it. */
  selectedCount(folder: ZephyrFolder): number {
    const folderIds = new Set(this.folderIdsIn(folder));
    let count = 0;
    for (const row of this.selectedCases.values()) {
      if (folderIds.has(row.folderId)) {
        count += 1;
      }
    }
    return count;
  }

  onTestCasesAction(event: { type?: string; data?: ZephyrAccordionRow[] }): void {
    if (event?.type !== 'row_select' || !Array.isArray(event.data)) {
      return;
    }
    const visibleIds = new Set(this.testCaseView.rows.map((row) => row._id));
    const selectedNow = new Set(event.data.map((row) => row._id));
    for (const id of visibleIds) {
      if (!selectedNow.has(id)) {
        this.selectedCases.delete(id);
      }
    }
    for (const row of event.data) {
      this.selectedCases.set(row._id, row);
    }
    this.retainedSelectionIds = [...this.selectedCases.keys()];
    this.selectedTestCaseCount = this.selectedCases.size;
    this.selectedFolderCount = new Set(
      [...this.selectedCases.values()].map((row) => row.folderId)
    ).size;
  }

  private loadFieldMapping(): void {
    if (this.selectedTestCaseCount === 0 || this.isLoadingMapping || !this.projectId || !this.connectionId) {
      return;
    }
    this.isLoadingMapping = true;
    this.mappingError = '';
    forkJoin({
      preview: this.restService.getImportPreviewColumns(
        this.projectId,
        this.connectionId,
        this.buildImportSelection(),
      ),
      saved: this.restService.getImportMapping(this.projectId, this.connectionId).pipe(
        catchError(() => of(null)),
      ),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ preview, saved }) => {
          const inner = preview?.data?.data ?? preview?.data ?? {};
          this.mappingFields = Array.isArray(inner.systemTestCasesFields) ? inner.systemTestCasesFields : [];
          const columns = this.toExtractedColumns(inner.extractedColumns);
          this.extractedColumns = columns;
          this.mappingColumns = columns.map((column) => column.field);
          this.applySavedOrAutoMapping(columns, saved?.data?.columnMappings ?? null);
          this.isLoadingMapping = false;
          this.step = 3;
          this.syncDialogSize();
        },
        error: (error: { error?: { message?: string }; message?: string }) => {
          this.isLoadingMapping = false;
          this.mappingError =
            error?.error?.message || error?.message || 'Failed to load columns. Please try again.';
        },
      });
  }

  private saveMapping(): void {
    if (!this.canContinueMapping || this.isImporting || !this.projectId || !this.connectionId) {
      return;
    }
    this.isImporting = true;
    this.mappingError = '';
    const columnMappings = this.activeColumnMappings;
    this.restService
      .saveImportMapping(this.projectId, this.connectionId, columnMappings, this.buildImportSelection())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isImporting = false;
          const inner = response?.data?.data ?? response?.data ?? {};
          const result: ZephyrImportedResult = {
            imported: true,
            connectionId: this.connectionId,
            connectionName: this.connectionName,
            fieldMappings: columnMappings,
            extractedTestCases: inner.extractedTestCases ?? [],
          };
          this.activeModal.close(result);
        },
        error: (error: { error?: { message?: string }; message?: string }) => {
          this.isImporting = false;
          this.mappingError = error?.error?.message || error?.message || 'Import failed. Please try again.';
        },
      });
  }

  /**
   * A folder whose every test case is selected is sent as a folder id.
   * Cases picked inside a folder that is not fully selected are sent by key.
   */
  private buildImportSelection(): { folderIds: string[]; TestCaseKeys: string[] } {
    const folderIds: string[] = [];
    const TestCaseKeys: string[] = [];
    const visit = (folder: ZephyrFolder): void => {
      if (this.isFolderFullySelected(folder)) {
        folderIds.push(folder.id);
        return;
      }
      for (const testCase of folder.testCases) {
        if (this.selectedCases.has(testCase.id) && testCase.key) {
          TestCaseKeys.push(testCase.key);
        }
      }
      folder.children.forEach(visit);
    };
    this.folders.forEach(visit);
    return { folderIds, TestCaseKeys };
  }

  private isFolderFullySelected(folder: ZephyrFolder): boolean {
    const cases = this.casesInFolder(folder);
    return cases.length > 0 && cases.every((testCase) => this.selectedCases.has(testCase.id));
  }

  private casesInFolder(folder: ZephyrFolder): ZephyrTestCase[] {
    return folder.children.reduce(
      (cases, child) => cases.concat(this.casesInFolder(child)),
      [...folder.testCases],
    );
  }

  private applySavedOrAutoMapping(
    columns: { source: string; field: string }[],
    savedMappings: Record<string, unknown> | null,
  ): void {
    const extractedFields = new Set(columns.map((column) => column.field));
    this.fieldMappings = {};
    if (savedMappings && Object.keys(savedMappings).length > 0) {
      for (const field of this.mappingFields) {
        const savedCol = this.savedColumnField(savedMappings[field.fieldKey]);
        if (savedCol && extractedFields.has(savedCol)) {
          this.fieldMappings[field.fieldKey] = savedCol;
        }
      }
      return;
    }
    for (const field of this.mappingFields) {
      const match = columns.find(
        (column) => column.field.trim().toLowerCase() === field.label.trim().toLowerCase(),
      );
      if (match) {
        this.fieldMappings[field.fieldKey] = match.field;
      }
    }
  }

  private savedColumnField(value: unknown): string {
    if (typeof value === 'string') {
      return value.trim();
    }
    if (value && typeof value === 'object' && 'field' in value) {
      const field = (value as { field?: unknown }).field;
      return typeof field === 'string' ? field.trim() : '';
    }
    return '';
  }

  private toExtractedColumns(raw: unknown): { source: string; field: string }[] {
    if (!Array.isArray(raw)) {
      return [];
    }
    const columns: { source: string; field: string }[] = [];
    for (const item of raw) {
      if (typeof item === 'string') {
        const field = item.trim();
        if (field) {
          columns.push({ source: 'FILE', field });
        }
        continue;
      }
      if (!item || typeof item !== 'object') {
        continue;
      }
      const record = item as { source?: unknown; field?: unknown };
      if (typeof record.field !== 'string') {
        continue;
      }
      const field = record.field.trim();
      if (!field) {
        continue;
      }
      const source = typeof record.source === 'string' && record.source.trim() ? record.source : 'FILE';
      columns.push({ source, field });
    }
    return columns;
  }

  private loadFolders(): void {
    if (!this.projectId || !this.connectionId) {
      this.foldersError = 'Select an ALM connection to load folders.';
      return;
    }
    this.isLoadingFolders = true;
    this.foldersError = '';
    this.restService
      .getZephyrFolders(this.projectId, this.connectionId)
      .pipe(
        switchMap((response) => this.attachTestCases(buildZephyrFolderTree(this.readFolderRecords(response)))),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (folders) => {
          this.folders = folders;
          this.isLoadingFolders = false;
          this.selectedFolderId = this.folders[0]?.id ?? '';
          this.refreshTestCases();
        },
        error: (error: Error) => {
          this.isLoadingFolders = false;
          this.folders = [];
          this.foldersError = error?.message || 'Failed to load folders.';
        },
      });
  }

  /** Each folder endpoint returns only its own cases, so every folder in the tree is requested. */
  private attachTestCases(folders: ZephyrFolder[]): Observable<ZephyrFolder[]> {
    const flat = this.flattenFolders(folders);
    if (!flat.length) {
      return of(folders);
    }
    return forkJoin(flat.map((folder) => this.casesForFolder(folder))).pipe(map(() => folders));
  }

  private casesForFolder(folder: ZephyrFolder, startAt = 0): Observable<ZephyrTestCase[]> {
    return this.restService.getZephyrTestCases(this.projectId, this.connectionId, folder.id, startAt).pipe(
      switchMap((response) => {
        const page = this.readTestCasePage(response);
        const cases = page.values.map((item) => this.toTestCase(item));
        const loaded = startAt + cases.length;
        if (page.isLast || cases.length === 0 || (page.total > 0 && loaded >= page.total)) {
          folder.testCases = startAt === 0 ? cases : folder.testCases.concat(cases);
          return of(folder.testCases);
        }
        folder.testCases = startAt === 0 ? cases : folder.testCases.concat(cases);
        return this.casesForFolder(folder, loaded);
      }),
      catchError(() => {
        folder.testCases = folder.testCases ?? [];
        return of(folder.testCases);
      }),
    );
  }

  private readTestCasePage(response: any): { values: any[]; isLast: boolean; total: number } {
    const payload = response?.data?.values ? response.data : response?.data?.data ?? response?.data ?? {};
    return {
      values: Array.isArray(payload?.values) ? payload.values : [],
      isLast: payload?.isLast !== false,
      total: Number(payload?.total) || 0,
    };
  }

  private toTestCase(item: any): ZephyrTestCase {
    const status = typeof item?.status === 'string' ? item.status : item?.status?.name;
    return {
      id: String(item?.id ?? item?.key ?? ''),
      key: String(item?.key ?? item?.id ?? ''),
      name: String(item?.name ?? ''),
      status: status || 'Draft',
    };
  }

  private flattenFolders(folders: ZephyrFolder[]): ZephyrFolder[] {
    return folders.reduce(
      (all, folder) => all.concat(folder, this.flattenFolders(folder.children)),
      [] as ZephyrFolder[],
    );
  }

  private readFolderRecords(response: any): ZephyrFolderRecord[] {
    const payload = response?.data?.values ? response.data : response?.data?.data ?? response?.data ?? response ?? {};
    const values = Array.isArray(payload?.values) ? payload.values : [];
    return values.filter((item: ZephyrFolderRecord) => item && item.id != null && item.name);
  }

  private refreshTestCases(): void {
    const folder = this.findFolder(this.folders, this.selectedFolderId);
    const rows = folder ? this.collectRows(folder) : [];
    this.testCaseView = {
      columns: this.accordionColumns.map((column) => ({ ...column })),
      rows,
    };
  }

  private findFolder(folders: ZephyrFolder[], id: string): ZephyrFolder | null {
    for (const folder of folders) {
      if (folder.id === id) {
        return folder;
      }
      const nested = this.findFolder(folder.children, id);
      if (nested) {
        return nested;
      }
    }
    return null;
  }

  /** Selected folder cases, then every case in its child folders. */
  private collectRows(folder: ZephyrFolder): ZephyrAccordionRow[] {
    const own = folder.testCases.map((testCase) => this.toRow(folder, testCase));
    return folder.children.reduce(
      (rows, child) => rows.concat(this.collectRows(child)),
      own,
    );
  }

  private toRow(folder: ZephyrFolder, testCase: ZephyrTestCase): ZephyrAccordionRow {
    return {
      _id: testCase.id,
      userStoryId: folder.name,
      key: testCase.key,
      testCaseId: testCase.key,
      testCase: testCase.name,
      status: testCase.status,
      folderName: folder.name,
      folderId: folder.id,
    };
  }

  private countTestCases(folder: ZephyrFolder): number {
    return folder.testCases.length + folder.children.reduce(
      (total, child) => total + this.countTestCases(child),
      0,
    );
  }

  private folderIdsIn(folder: ZephyrFolder): string[] {
    return [folder.id, ...folder.children.flatMap((child) => this.folderIdsIn(child))];
  }

  /** Field mapping is a narrower dialog than the folder browser. */
  private syncDialogSize(compact = this.step === 3): void {
    const dialog = this.document.querySelector('.import-zephyr-modal-window .modal-dialog');
    dialog?.classList.toggle('iz-dialog--compact', compact);
  }
}
